import { useMemo, useState } from "react";
import { Card, HorizBox, Pad, PadBox, Separator } from "component/basics"
import { ConversationScreen } from "component/screen";
import { Heading, TextField } from "component/text"
import { useCollection, useDatastore, useObject } from "util/datastore";
import { declareTable, Table } from "util/stdtypes";
import { useIsAdmin } from "component/admin"
import { CTAButton } from "component/button";
import { Catcher } from "system/catcher";
import { FormField, Toggle } from "component/form";
import { ARTICLE_ICON_TYPES, ReadingList } from "feature/topic/reading-list";
import { DropDownSelector } from "component/popup";

export type RelatedArticle = {
    key: string;
    id?: string;
    title: string;
    text?: string;
    image?: string;
    picture?: string;
    type: string;
    url: string;
    deleted?: boolean;
    date?: string;
    published?: boolean;
};

const relatedArticlesTable = declareTable<RelatedArticle>('relatedArticles');

export const EditorialReadingListFeature = {
    key: 'editorialReadinglist',
    name: 'Editorial Reading List',
    subscreens: {
        relatedArticlesAdmin: () => <RelatedArticlesAdmin />,
    },
    config: {
        topicBottomWidgets: [EditorialReadingListWidget],
    }
}

export function EditorialReadingListWidget() {
    const isAdmin = useIsAdmin();
    const datastore = useDatastore();
    const articles = useCollection(relatedArticlesTable, { filter: { published: true } });
    
    const formattedArticles = useMemo(
        () =>
            articles.map(({ title, text, image, picture, ...rest }: RelatedArticle) => ({
                title: text || title,
                image: picture || image,
                ...rest,
            })),
        [articles]
    );
    
    return <>
        {isAdmin && (
            <PadBox horiz={20} top={32}>
                <CTAButton type='primary' label='Manage related articles' onPress={datastore.needsLogin(() => datastore.pushSubscreen('relatedArticlesAdmin', {}))} />
            </ PadBox>
        )}
        <ReadingList articles={formattedArticles} />
    </>
}

function Article({ article }) {
    const datastore = useDatastore();
    const selectedArticle = useObject(relatedArticlesTable, article.key);
    const isChosen = !!selectedArticle && !!selectedArticle.published;

    async function onChange(selected) {
        await datastore.callServerAsync('topics', selected ? 'publishArticleMetaData' : 'unpublishArticleMetaData', { article: {
            id: article.id,
            key: article.key,
            title: article.text || article.title,
            image: article.picture || article.image,
            type: article.type,
            url: article.url,
            deleted: article.deleted,
            date: article.date,
        } });
    }

    async function onDelete() {
        await datastore.callServerAsync('topics', 'deleteArticleMetaData', { articleKey: article.key });
    }

    return <PadBox bottom={20} horiz={20}>
        <Card>
            <HorizBox spread>
                <Toggle text={article.text} value={isChosen} onChange={onChange} />
                <Pad />
                <CTAButton label='Delete' onPress={onDelete} />
            </HorizBox>
        </Card>
    </PadBox>
}

export function RelatedArticlesAdmin() {
    const datastore = useDatastore();
    const relatedArticles = useCollection(relatedArticlesTable, { filter: { deleted: false } });
    const [articleUrl, setArticleUrl] = useState('');
    const [type, setType] = useState('text');
    const [isLoading, setIsLoading] = useState(false);

    async function onAddAsync() {
        setIsLoading(true);
        await datastore.callServerAsync('topics', 'addArticleMetaData', {url: articleUrl, type });
        setArticleUrl('');
        setIsLoading(false);
    }

    return (
        <ConversationScreen>
            <Pad />
            <PadBox horiz={20}><Heading label='Manage related articles' />
                <Pad />
                <FormField label='Add article'>
                    <TextField multiline={false} testID="article-url-field" autoFocus value={articleUrl} onChange={setArticleUrl}
                        placeholder='Paste url here' />
                </FormField>
                <Pad />
                <DropDownSelector label='Article type'
                    value={type} onChange={(value) => setType(value)}
                    options={[
                        {key: ARTICLE_ICON_TYPES.text, label: 'Text'},
                        {key: ARTICLE_ICON_TYPES.media, label: 'Media'},
                        {key: ARTICLE_ICON_TYPES.audio, label: 'Audio'}
                    ]} />
                <Pad />
                <CTAButton label='Add article' onPress={onAddAsync} disabled={isLoading} />
            </PadBox>
            <Pad />
            <Separator />
            <Pad />
            {relatedArticles?.map(article =>
                <Catcher key={article.key}>
                    <Article article={article} />
                </Catcher>
            )}
        </ConversationScreen>
    )
}